import React, { useEffect, useMemo, useRef, useState } from "react";
import { Animated, Easing, StyleSheet, Text, View } from "react-native";
import { Credentials } from "../storage/credentials";
import { imageSource, JellyfinItem } from "../api/jellyfin";
import { colors, interaction, motion, radius, spacing, systemSansFont } from "../theme/tokens";
import { FocusablePressable } from "./FocusablePressable";
import { CardHoverOverlay } from "./CardHoverOverlay";
import { MediaImage } from "./MediaImage";
import { isTelevisionRuntime } from "../utils/platformCapabilities";
import { hasMeaningfulResume } from "../utils/resumeProgress";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";
import { usePlayedStateToggle } from "../utils/usePlayedStateToggle";
import { focusRegisteredNode, getFocusModality, subscribeFocusModality, subscribeFocusTransitions } from "../tvNavigation/registry";
import { AnimeLocalization, getAnimeLocalization } from "../utils/animeLocalization";

type Props = {
  item: JellyfinItem;
  credentials: Credentials;
  width: number;
  onPress: () => void;
  onPlay?: () => void;
  itemDetail?: boolean;
  onFocusChange?: (focused: boolean) => void;
  hovered?: boolean;
  onHoverChange?: (hovered: boolean) => void;
  presentation?: "default" | "continue";
  itemDetailAccent?: string;
  focusId?: string;
  focusGroup?: string;
  focusDestinations?: import('../tvNavigation/core').FocusDestinations;
  animeLocalization?: AnimeLocalization;
};

function formatRemaining(item: JellyfinItem) {
  const duration = item.RunTimeTicks ?? 0;
  const position = item.UserData?.PlaybackPositionTicks ?? 0;
  if (duration <= position) return "";
  const minutes = Math.max(1, Math.round((duration - position) / 600000000));
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h ${minutes % 60}m left`;
  return `${minutes}m left`;
}

function formatRuntime(item: JellyfinItem) {
  const ticks = item.RunTimeTicks ?? 0;
  if (ticks <= 0) return "";
  const minutes = Math.max(1, Math.round(ticks / 600000000));
  return `${minutes}m`;
}

function LandscapeMediaCardComponent({
  item,
  credentials,
  width,
  onPress,
  onPlay,
  itemDetail = false,
  onFocusChange,
  hovered: controlledHovered,
  onHoverChange,
  presentation = "default",
  itemDetailAccent,
  focusId,
  focusGroup,
  focusDestinations,
  animeLocalization: localizationOverride,
}: Props) {
  const television = isTelevisionRuntime();
  const playedState = usePlayedStateToggle(item, credentials);
  const animeLocalization = localizationOverride ?? getAnimeLocalization(item);
  const [backdropFailed, setBackdropFailed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [overlayHovered, setOverlayHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [controllerActionMode, setControllerActionMode] = useState(false);
  const hoverExitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (hoverExitTimer.current) clearTimeout(hoverExitTimer.current);
    },
    [],
  );
  const cardFocusId = focusId ?? `${focusGroup ?? 'landscape-cards'}.${item.Id ?? item.Name ?? 'unknown'}`;
  useEffect(() => subscribeFocusModality((next) => {
    if (next === 'pointer') setControllerActionMode(false);
  }), []);
  useEffect(() => {
    if (!controllerActionMode) return undefined;
    return subscribeFocusTransitions((transition) => {
    if (!controllerActionMode || !transition.toId) return;
    if (transition.toId === cardFocusId && transition.fromId !== cardFocusId) {
      setControllerActionMode(false);
      return;
    }
    if (transition.toId !== cardFocusId && !transition.toId.startsWith(`${cardFocusId}.`)) setControllerActionMode(false);
    });
  }, [cardFocusId, controllerActionMode]);
  const enterCard = () => {
    if (hoverExitTimer.current) clearTimeout(hoverExitTimer.current);
    setHovered(true);
    onHoverChange?.(true);
  };
  const leaveCard = () => {
    setHovered(false);
    hoverExitTimer.current = setTimeout(() => {
      setOverlayHovered(false);
      onHoverChange?.(false);
    }, 70);
  };
  const interactionSuppressed = controlledHovered === false;
  const activeHovered =
    !interactionSuppressed && (hovered || controlledHovered === true);
  const cardProgress = useRef(new Animated.Value(0)).current;
  const cardActive =
    controllerActionMode || (!interactionSuppressed && (activeHovered || overlayHovered || focused));
  const focusPlayAction = (attempt = 0) => {
    requestAnimationFrame(() => {
      if (focusRegisteredNode(`${cardFocusId}.play`) || attempt >= 5) return;
      focusPlayAction(attempt + 1);
    });
  };
  const handleControllerCardPress = () => {
    setControllerActionMode(true);
    focusPlayAction();
  };
  const handleCardPress = () => {
    if (getFocusModality() === 'pointer' || !onPlay) {
      onPress();
      return;
    }
    handleControllerCardPress();
  };
  const handleCardPlay = () => { setControllerActionMode(false); (onPlay ?? onPress)(); };
  const handleCardInfo = () => { setControllerActionMode(false); onPress(); };
  useEffect(() => {
    cardProgress.stopAnimation();
    const animation = Animated.timing(cardProgress, {
      toValue: cardActive ? 1 : 0,
      duration: cardActive ? motion.controlEnter : motion.controlExit,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });
    animation.start();
    return () => animation.stop();
  }, [cardActive, cardProgress]);
  const handleOverlayHover = (value: boolean) => {
    if (value) {
      setOverlayHovered(true);
      enterCard();
    }
  };
  const height = Math.round(width * 0.5625);
  const cardScale = cardProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [1, itemDetail ? 1 : 1.02],
  });
  const cardTranslateY = cardProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, itemDetail ? 0 : -3],
  });
  const kind =
    item.Type === "Episode" && item.ImageTags?.Thumb
      ? "Thumb"
      : item.Type === "Episode" || backdropFailed
        ? "Primary"
        : "Backdrop";
  const episodeName = item.Type === "Episode"
    ? (item.Name ?? "").replace(/^S\d+E\d+\s*[-–:]\s*/i, "")
    : "";
  const detail =
    item.Type === "Episode" ? `E${item.IndexNumber ?? ""}: ${episodeName}` : "";
  const episodeIndex = item.Type === "Episode"
    ? [item.ParentIndexNumber ? `S${item.ParentIndexNumber}` : "", item.IndexNumber ? `E${item.IndexNumber}` : ""].filter(Boolean).join(" ")
    : "";
  const remaining = formatRemaining(item);
  const itemDetailRuntime = formatRuntime(item);
  const itemDetailAccentRgb = itemDetailAccent
    ?.match(/\d+/g)
    ?.slice(0, 3)
    .join(",");
  const captionGradientId = `episode-caption-${(item.Id ?? item.Name ?? "card").replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const meaningfulResume = hasMeaningfulResume(item);
  const artworkSource = useMemo(() => imageSource(
    credentials,
    item,
    kind,
    // A 1x TV request is sufficient for the 1080p surface and halves decoded
    // bitmap memory across the many mounted Home shelf cards.
    Math.min(610, Math.round(width * (television ? 1 : 2))),
    Math.min(344, Math.round(height * (television ? 1 : 2))),
    82,
  ), [credentials.accessToken, credentials.serverUrl, height, item.Id, item.ImageTags?.Thumb, item.Type, kind, television, width]);
  const continueDurationLabel = meaningfulResume ? remaining : itemDetailRuntime;
  const continueTitle = item.Type === "Episode"
    ? item.SeriesName ?? item.Name ?? "Untitled"
    : item.Name ?? "Untitled";
  return (
    <Animated.View
      style={[
        styles.animatedRoot,
        itemDetail && styles.itemDetailRoot,
        activeHovered && styles.hovered,
        focused && styles.focused,
      ]}
    >
      <FocusablePressable
        focusId={cardFocusId}
        focusGroup={focusGroup}
        focusDestinations={focusDestinations}
        focusRole="card"
        focusHalo={false}
        testID={`landscape-media-card-${item.Id ?? item.Name ?? "unknown"}`}
        onPress={handleCardPress}
        onControllerPress={onPlay ? handleControllerCardPress : undefined}
        onHoverIn={enterCard}
        onHoverOut={leaveCard}
        onFocus={() => {
          setFocused(true);
          onFocusChange?.(true);
        }}
        onBlur={() => {
          setFocused(false);
          onFocusChange?.(false);
        }}
        style={() => [
          styles.root,
          itemDetail && styles.itemDetailCardRoot,
          { width },
        ]}
      >
        {() => (
          <>
            <View style={{ width, height, overflow: "visible" }}>
            <Animated.View
              style={[
                styles.imageShell,
                {
                  position: "absolute",
                  width,
                  height,
                  left: 0,
                  top: 0,
                  transform: [{ translateY: cardTranslateY }, { scale: cardScale }],
                },
                cardActive && styles.imageShellHovered,
                focused && !controllerActionMode && styles.imageShellFocused,
              ]}
            >
              <MediaImage
                source={artworkSource}
                onError={() => setBackdropFailed(true)}
                fallbackLabel={item.Name}
                resizeMode="cover"
                style={StyleSheet.absoluteFillObject}
              />
              {animeLocalization ? <View pointerEvents="none" style={styles.animeLocalizationBadge}><Text style={styles.animeLocalizationText}>{animeLocalization}</Text></View> : null}
              {typeof item.UserData?.PlayedPercentage === "number" &&
              meaningfulResume ? (
                <View style={styles.track}>
                  <View
                    style={[
                      styles.progress,
                      {
                        width: Math.round(
                          (width *
                            Math.min(item.UserData.PlayedPercentage, 100)) /
                            100,
                        ),
                      },
                    ]}
                  />
                </View>
              ) : null}
              {presentation === "continue" && continueDurationLabel && cardActive ? (
                <View pointerEvents="none" style={[styles.continueDurationBadge, television && styles.continueDurationBadgeTelevision]}>
                  <Text numberOfLines={1} style={[styles.continueDurationBadgeText, television && styles.continueDurationBadgeTextTelevision]}>{continueDurationLabel}</Text>
                </View>
              ) : null}
              {itemDetail ? (
                <>
                  <Svg
                    pointerEvents="none"
                    width={width}
                    height={76}
                    style={styles.itemDetailCaptionShade}
                  >
                    <Defs>
                      <LinearGradient id={captionGradientId} x1="0" y1="0" x2="0" y2="1">
                        <Stop offset="0" stopColor="#080808" stopOpacity="0" />
                        <Stop offset="0.42" stopColor="#080808" stopOpacity="0.56" />
                        <Stop offset="1" stopColor="#080808" stopOpacity="0.94" />
                      </LinearGradient>
                    </Defs>
                    <Rect width={width} height={76} fill={`url(#${captionGradientId})`} />
                  </Svg>
                  <Animated.View
                    pointerEvents="none"
                    style={[
                      styles.itemDetailMetaOverlay,
                      television && styles.itemDetailMetaOverlayTelevision,
                      {
                        transform: [
                          {
                            translateY: cardProgress.interpolate({
                              inputRange: [0, 1],
                              outputRange: [0, -2],
                            }),
                          },
                        ],
                      },
                    ]}
                  >
                    {item.Type === "Episode" && item.IndexNumber != null ? (
                      <View
                        style={[
                          styles.itemDetailEpisodeBadge,
                          television && styles.itemDetailEpisodeBadgeTelevision,
                          itemDetailAccentRgb && {
                            borderColor: `rgba(${itemDetailAccentRgb},0.46)`,
                            backgroundColor: `rgba(${itemDetailAccentRgb},0.14)`,
                          },
                        ]}
                      >
                        <Text style={[styles.itemDetailEpisodeBadgeText, television && styles.itemDetailEpisodeBadgeTextTelevision]}>
                          {`E${item.IndexNumber}`}
                        </Text>
                      </View>
                    ) : null}
                    <Text numberOfLines={1} style={[styles.itemDetailTitle, television && styles.itemDetailTitleTelevision]}>
                      {episodeName || item.Name || "Untitled"}
                    </Text>
                    {itemDetailRuntime ? (
                      <Text style={[styles.itemDetailRuntime, television && styles.itemDetailRuntimeTelevision]}>{itemDetailRuntime}</Text>
                    ) : null}
                  </Animated.View>
                </>
              ) : null}
              <CardHoverOverlay
                item={item}
                credentials={credentials}
                imageKind={kind}
                visible={cardActive}
                focusId={cardFocusId}
                focusGroup={focusGroup}
                cardFocusDestinations={focusDestinations}
                controllerActionMode={controllerActionMode}
                width={width}
                height={height}
                landscape
                showRuntime={!itemDetail && presentation !== "continue"}
                onHoverChange={handleOverlayHover}
                onPlay={handleCardPlay}
                onInfo={handleCardInfo}
                played={playedState.played}
                playedPending={playedState.pending}
                onTogglePlayed={playedState.supported ? () => {
                  void playedState.togglePlayed();
                } : undefined}
              />
            </Animated.View>
            </View>
            {itemDetail ? null : presentation === "continue" ? (
              <>
                <View style={styles.continueMetaRow}>
                  <Text numberOfLines={1} style={[styles.continueTitle, item.Type !== "Episode" && styles.continueMovieTitle, television && styles.continueTitleTelevision]}>
                    {continueTitle}
                  </Text>
                  {episodeIndex ? <Text numberOfLines={1} style={[styles.continueEpisodeIndex, television && styles.continueEpisodeIndexTelevision]}>{episodeIndex}</Text> : null}
                </View>
                {episodeName ? (
                  <View style={styles.continueInfoRow}>
                    {episodeName ? <Text numberOfLines={1} style={[styles.continueEpisodeName, television && styles.continueEpisodeNameTelevision]}>{episodeName}</Text> : null}
                  </View>
                ) : null}
              </>
            ) : (
              item.Type === "Episode" ? (
                <>
                <Text numberOfLines={1} style={styles.title}>
                  {item.SeriesName ?? item.Name ?? "Untitled"}
                </Text>
                <Text numberOfLines={1} style={styles.detail}>
                  {detail}
                </Text>
                </>
              ) : (
                <Text numberOfLines={1} style={[styles.detail, styles.movieTitle]}>
                  {item.Name ?? "Untitled"}
                </Text>
              )
            )}
          </>
        )}
      </FocusablePressable>
    </Animated.View>
  );
}

export const LandscapeMediaCard = React.memo(LandscapeMediaCardComponent, (previous, next) =>
  previous.item === next.item &&
  previous.credentials.accessToken === next.credentials.accessToken &&
  previous.credentials.serverUrl === next.credentials.serverUrl &&
  previous.width === next.width &&
  previous.itemDetail === next.itemDetail &&
  previous.itemDetailAccent === next.itemDetailAccent &&
  previous.presentation === next.presentation &&
  previous.hovered === next.hovered,
);

const styles = StyleSheet.create({
  animatedRoot: { marginRight: spacing.sm, borderRadius: radius.medium },
  itemDetailRoot: { marginRight: 0 },
  root: { paddingBottom: spacing.sm, borderRadius: radius.medium },
  itemDetailCardRoot: { paddingBottom: 0 },
  hovered: { zIndex: 4 },
  focused: { zIndex: 5 },
  imageShell: {
    overflow: "hidden",
    borderRadius: radius.medium,
    backgroundColor: colors.surfaceRaised,
  },
  imageShellHovered: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "rgba(255,255,255,0.20)",
    shadowColor: "#000",
    shadowOpacity: 0.62,
    shadowRadius: 18,
    elevation: interaction.raisedElevation,
  },
  imageShellFocused: { borderWidth: interaction.focusBorderWidth, borderColor: colors.brandBright, shadowColor: colors.brand, shadowOpacity: 0.62, shadowRadius: 10, elevation: interaction.raisedElevation },
  title: {
    color: colors.textMuted,
    marginTop: 6,
    fontSize: 11,
    fontWeight: "500",
  },
  detail: {
    color: colors.textStrong,
    marginTop: 2,
    fontSize: 13,
    lineHeight: 16,
    fontWeight: "500",
  },
  movieTitle: { marginTop: 6 },
  itemDetailCaptionShade: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  itemDetailMetaOverlay: {
    position: "absolute",
    left: 10,
    right: 10,
    bottom: 9,
    zIndex: 2,
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
  },
  itemDetailMetaOverlayTelevision: { left: 12, right: 12, bottom: 11, gap: 8 },
  itemDetailEpisodeBadge: {
    minWidth: 29,
    height: 22,
    paddingHorizontal: 6,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "rgba(255,255,255,0.2)",
    borderRadius: 5,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.07)",
  },
  itemDetailEpisodeBadgeTelevision: { minWidth: 34, height: 26, paddingHorizontal: 7 },
  itemDetailEpisodeBadgeText: {
    color: "rgba(255,255,255,0.72)",
    fontFamily: systemSansFont,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "800",
  },
  itemDetailEpisodeBadgeTextTelevision: { fontSize: 13, lineHeight: 17 },
  itemDetailTitle: {
    flex: 1,
    minWidth: 0,
    color: colors.textStrong,
    fontFamily: systemSansFont,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "600",
  },
  itemDetailTitleTelevision: { fontSize: 16, lineHeight: 22 },
  itemDetailRuntime: {
    color: "rgba(255,255,255,0.48)",
    fontFamily: systemSansFont,
    fontSize: 11,
    lineHeight: 15,
    fontWeight: "600",
  },
  itemDetailRuntimeTelevision: { fontSize: 13, lineHeight: 18 },
  continueMetaRow: {
    marginTop: 8,
    flexDirection: "row",
    alignItems: "center",
  },
  continueTitle: {
    color: colors.textMuted,
    fontFamily: systemSansFont,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "500",
    flexShrink: 1,
  },
  continueMovieTitle: { color: colors.textStrong, fontSize: 14 },
  continueTitleTelevision: { fontSize: 15, lineHeight: 20 },
  continueInfoRow: {
    marginTop: 2,
    flexDirection: "row",
    alignItems: "center",
  },
  continueEpisodeIndex: {
    marginLeft: 6,
    paddingHorizontal: 4,
    color: "rgba(255,255,255,0.72)",
    fontFamily: systemSansFont,
    fontSize: 10,
    lineHeight: 14,
    fontWeight: "500",
    flexShrink: 0,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "rgba(255,255,255,0.20)",
    borderRadius: 3,
    backgroundColor: "rgba(255,255,255,0.07)",
  },
  continueEpisodeIndexTelevision: { fontSize: 13, lineHeight: 18, paddingHorizontal: 5 },
  continueEpisodeName: {
    color: colors.textStrong,
    fontFamily: systemSansFont,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "500",
    flexShrink: 1,
  },
  continueDurationBadge: {
    position: "absolute",
    right: 8,
    bottom: 8,
    zIndex: 8,
    paddingHorizontal: 6,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "rgba(255,255,255,0.18)",
    borderRadius: 4,
    backgroundColor: "rgba(8,8,8,0.68)",
  },
  continueDurationBadgeTelevision: { right: 10, bottom: 10, paddingHorizontal: 7 },
  continueDurationBadgeText: {
    color: "rgba(255,255,255,0.76)",
    fontFamily: systemSansFont,
    fontSize: 10,
    lineHeight: 14,
    fontWeight: "500",
  },
  continueDurationBadgeTextTelevision: { fontSize: 13, lineHeight: 18 },
  continueEpisodeNameTelevision: { fontSize: 17, lineHeight: 22 },
  continueMetaTelevision: { fontSize: 15, lineHeight: 22 },
  track: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 4,
    zIndex: 8,
    backgroundColor: "rgba(255,255,255,0.2)",
  },
  progress: { height: 4, backgroundColor: colors.brand },
  animeLocalizationBadge: {
    position: "absolute", top: 8, left: 8, paddingHorizontal: 8, paddingVertical: 4,
    borderRadius: 5, backgroundColor: "rgba(12, 12, 14, 0.86)", borderWidth: 1,
    borderColor: "rgba(255,255,255,0.28)", zIndex: 9,
  },
  animeLocalizationText: { color: colors.text, fontFamily: systemSansFont, fontSize: 11, fontWeight: "800", letterSpacing: 0.8 },
});
